import React, { useCallback, useState } from 'react'
import { Field, FieldArray, Form, Formik } from 'formik'
import { debounce } from 'lodash'
import Button from './common/Button'
import Input from './common/Input'
import Card from './common/Card'
import { format } from '../utils/number'
import { useLoad } from '../hooks/request'
import { domain, PRODUCT_DETAIL, PRODUCT_LIST, SUPPLIER_LIST } from '../urls'
import Select from './common/Select'
import { currency, getMeasurement, paid, paymentMethod } from '../utils/types'
import Table from './common/Table'
import { required } from '../utils/validators'
import { useModal } from '../hooks/modal'
import DeleteModal from './common/DeleteModal'
import DocumentsProductUpdate from './DocumentsProductUpdate'
import DocumentsProductCreate from './DocumentsProductCreate'
import NewSupplierSelect from './common/NewSupplier'
import ReactModal from './common/ReactModal'

export default function DocumentsForm({ loading, onCancel, onSubmit, initialValues = {} }) {
    const [params, setParams] = useState({})
    const [selectedImage, setSelectedImage] = useState(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const productLoad = useLoad({ url: PRODUCT_LIST, params }, [params])
    const productItem = productLoad.response ? productLoad.response.results : []
    const products = productItem.map((item) => ({
        ...item,
        quantity: '',
        count: item?.count || '',
        price: item?.price || '',
        priceSale: item?.priceSale || '',
        priceTransfer: item?.priceTransfer || '',
    }))

    const suppliers = useLoad({ url: SUPPLIER_LIST, params: { size: 1000 } })

    const [showProductCreate, hideProductCreate] = useModal((arrayHelpers) => <DocumentsProductCreate onCancel={() => hideProductCreate()} refresh={productLoad.request} arrayHelpers={arrayHelpers} />)
    const [showProductEdit, hideProductEdit] = useModal((props) => <DocumentsProductUpdate onCancel={() => hideProductEdit()} refresh={productLoad.request} item={props.item} arrayHelp={props.arrayHelp} />)

    const [showDeleteProduct, hideDeleteProduct] = useModal((item) => <DeleteModal onCancel={() => hideDeleteProduct()} refresh={productLoad.request} url={PRODUCT_DETAIL.replace('{id}', item?.id)} name={item?.name} />)

    const debouncedSearch = debounce((search) => setParams((prev) => ({ ...prev, search })), 500)
    const handleSearch = useCallback(({ target: { value } }) => debouncedSearch(value), [debouncedSearch])

    // Default image placeholder
    const defaultImageStyle = {
        width: '40px',
        height: '40px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#999',
        fontSize: '12px',
        textAlign: 'center'
    }

    const imageStyle = {
        width: '40px',
        height: '40px',
        objectFit: 'cover',
        borderRadius: '4px',
        border: '1px solid #ddd'
    }

    const handleSubmit = (vals, actions) => {
        const data = {
            ...vals,
            dateReturn: vals.dateReturn || undefined,
            invoice: vals.invoice ? +vals.invoice : undefined,
            wholesale: vals.wholesale ? +vals.wholesale : undefined,
            markup: vals.markup ? +vals.markup : undefined,
            vat: vals.vat ? +vals.vat : undefined,
            expenses: vals.expenses ? +vals.expenses : undefined,
            products: vals.products.map((item) => ({
                ...item,
                quantity: item.quantity ? item.quantity : 0,
                price: item.price ? item.price : 0,
                priceSale: item.priceSale ? item.priceSale : 0,
                priceTransfer: item.priceTransfer ? item.priceTransfer : 0,
            })),
        }
        console.log(data, 'data bu')
        onSubmit(data, actions)
    }

    return (
        <Formik
            enableReinitialize
            onSubmit={handleSubmit}
            initialValues={{
                products: JSON.parse(localStorage.getItem('items')) || [],
                supplier: JSON.parse(localStorage.getItem('formData'))?.supplier || '',
                dateReturn: '',
                currency: JSON.parse(localStorage.getItem('formData'))?.currency || 'sum',
                paid: '',
                paymentMethod: '',
                invoice: '',
                wholesale: '',
                markup: '',
                sales: '',
                margin: '',
                vat: '',
                note: '',
                expenses: '',
                ...initialValues,
            }}
        >
            {({ values, setFieldValue }) => {
                const totals = values.products.reduce(
                    (acc, product) => {
                        const quantity = product.quantity ? +product.quantity : 0
                        const price = product.price ? +product.price : 0
                        const priceSale = product.priceSale ? +product.priceSale : 0
                        acc.totalQuantity += quantity
                        acc.totalPurchaseSum += quantity * price
                        acc.totalSaleSum += quantity * priceSale
                        return acc
                    },
                    { totalQuantity: 0, totalPurchaseSum: 0, totalSaleSum: 0 }
                )

                return (
                    <Form>
                        <div style={{ padding: '1rem', boxShadow: '0px 0px 1px 0px rgba(0,0,0,0.75)' }}>
                            <div className="columns">
                                <div className="column">
                                    <label htmlFor="supplier">Поставщик*</label>
                                    <Field name="supplier" validate={required}>
                                        {({ field, meta }) => (
                                            <div>
                                                <NewSupplierSelect
                                                    name={field.name}
                                                    onChange={(selectedOption) => {
                                                        const supplierId = selectedOption?.id || ''
                                                        setFieldValue('supplier', supplierId)
                                                        const formData = JSON.parse(localStorage.getItem('formData')) || {}
                                                        formData.supplier = supplierId
                                                        localStorage.setItem('formData', JSON.stringify(formData))
                                                    }}
                                                    value={suppliers?.response?.results?.find((item) => item.id === field.value)}
                                                    options={suppliers?.response?.results}
                                                    placeholder="Выбор поставщика"
                                                    disabled={loading}
                                                />
                                                {meta.touched && meta.error && (
                                                    <div className="has-text-danger is-size-7 mt-1">{meta.error}</div>
                                                )}
                                            </div>
                                        )}
                                    </Field>
                                </div>
                                <div className="column">
                                    <Select
                                        validate={required}
                                        empty
                                        options={currency}
                                        optionValue="value"
                                        name="currency"
                                        label="Валюта*"
                                        disabled={JSON.parse(localStorage.getItem('items'))?.length > 0}
                                        onChange={(e) => {
                                            const selectedCurrency = e.target.value
                                            setFieldValue('currency', selectedCurrency)
                                            setFieldValue('products', [])
                                            const formData = JSON.parse(localStorage.getItem('formData')) || {}
                                            formData.currency = selectedCurrency
                                            localStorage.setItem('formData', JSON.stringify(formData))
                                        }}
                                    />
                                </div>

                                <div className="column">
                                    <Select options={paid} optionValue="value" name="paid" label="Оплачено*" disabled={loading} />
                                </div>
                                <div className="column">
                                    <Select options={paymentMethod} optionValue="value" name="paymentMethod" label="Способ оплаты*" disabled={loading} />
                                </div>
                            </div>
                        </div>
                        <br />

                        <Card>
                            <FieldArray
                                name="products"
                                render={(arrayHelpers) => {
                                    console.log('values')

                                    return (
                                        <>
                                            <Table
                                                showEmptyMessage={false}
                                                columns={{ name: 'Наименование товара', remains: 'Остаток', quantity: 'Количество', measurement: 'Ед. изм.', price: 'Цена поступление', price_transfer: 'Оптом цена', price_sale: 'Цена за единицу', summ: 'Сумма', actions: '' }}
                                                items={values.products}
                                                renderItem={(item, index) => {
                                                    console.log(item, 'bu endi nimadi')
                                                    const productCurrency = item?.paymentType
                                                    const price = item?.price || productCurrency === 'sum' ? item?.sumPrice : item?.usdPrice
                                                    const count = item?.quantity || 0

                                                    const bodyPrice = productCurrency === 'sum' ? item?.sumPrice : item?.usdPrice
                                                    const SalePrice = productCurrency === 'sum' ? item?.sumPriceSale : item?.usdPriceSale
                                                    const SaleOptomPrice = productCurrency === 'sum' ? item?.sumPriceTransfer : item?.usdPriceTransfer
                                                    const sum = format(count * price)

                                                    return (
                                                        <tr key={item.id}>
                                                            <td>{item.name}</td>
                                                            <td>{item.count}</td>
                                                            <td>
                                                                <Input
                                                                    type="number"
                                                                    className="is-size-7"
                                                                    name={`products[${index}].quantity`}
                                                                    value={item.quantity || ''}
                                                                    onChange={(value) => {
                                                                        const parsedQuantity = value === '' ? '' : parseFloat(value) || 0
                                                                        setFieldValue(`products[${index}].quantity`, parsedQuantity)
                                                                        const localItems = JSON.parse(localStorage.getItem('items')) || []
                                                                        const updatedItems = localItems.map((product) => (product.id === item.id ? { ...product, quantity: parsedQuantity } : product))
                                                                        localStorage.setItem('items', JSON.stringify(updatedItems))
                                                                    }}
                                                                    disabled={loading}
                                                                />
                                                            </td>
                                                            <td>{getMeasurement(item.measurement)}</td>
                                                            <td>
                                                                <Input
                                                                    value={bodyPrice}
                                                                    type="number"
                                                                    className="is-size-7"
                                                                    name={`products[${index}].price`}
                                                                    disabled={loading}
                                                                    onChange={(value) => {
                                                                        const newField = item?.paymentType === 'sum' ? 'sumPrice' : 'usdPrice'
                                                                        setFieldValue(`products[${index}].${newField}`, value)
                                                                        const localItems = JSON.parse(localStorage.getItem('items')) || []
                                                                        const updatedItems = localItems.map((product) => (product.id === item.id ? { ...product, [newField]: value } : product))
                                                                        localStorage.setItem('items', JSON.stringify(updatedItems))
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Input
                                                                    value={SaleOptomPrice}
                                                                    type="number"
                                                                    className="is-size-7"
                                                                    name={`products.${index}.priceTransfer`}
                                                                    disabled={loading}
                                                                    onChange={(value) => {
                                                                        const newField = item?.paymentType === 'sum' ? 'sumPriceTransfer' : 'usdPriceTransfer'
                                                                        setFieldValue(`products[${index}].${newField}`, value)
                                                                        const localItems = JSON.parse(localStorage.getItem('items')) || []
                                                                        const updatedItems = localItems.map((product) => (product.id === item.id ? { ...product, [newField]: value } : product))
                                                                        localStorage.setItem('items', JSON.stringify(updatedItems))
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Input
                                                                    value={SalePrice}
                                                                    type="number"
                                                                    className="is-size-7"
                                                                    name={`products.${index}.priceSale`}
                                                                    disabled={loading}
                                                                    onChange={(value) => {
                                                                        const newField = item?.paymentType === 'sum' ? 'sumPriceSale' : 'usdPriceSale'
                                                                        setFieldValue(`products[${index}].${newField}`, value)
                                                                        const localItems = JSON.parse(localStorage.getItem('items')) || []
                                                                        const updatedItems = localItems.map((product) => (product.id === item.id ? { ...product, [newField]: value } : product))
                                                                        localStorage.setItem('items', JSON.stringify(updatedItems))
                                                                    }}
                                                                />
                                                            </td>
                                                            <td>{sum ?? ''}</td>
                                                            <td>
                                                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                    <Button
                                                                        onClick={() => {
                                                                            const localItems = JSON.parse(localStorage.getItem('items')) || []
                                                                            const result = localItems.filter((e) => e.id !== item.id)
                                                                            localStorage.setItem('items', JSON.stringify(result))
                                                                            arrayHelpers.remove(index)
                                                                        }}
                                                                        icon="close-outline"
                                                                        className="is-danger is-outlined is-size-7"
                                                                        disabled={loading}
                                                                    />
                                                                    <Button onClick={() => showProductEdit({ item, arrayHelp: { index, arrayHelpers } })} icon="create-outline" className="is-size-7 is-outlined is-link" disabled={loading} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                }}
                                            />

                                            <div className="is-flex mt-6">
                                                <input type="search" className="input" onChange={handleSearch} />

                                                <div className="ml-2">
                                                    <Button onClick={() => showProductCreate(arrayHelpers)} icon="add-outline" className="is-link" disabled={loading} />
                                                    &nbsp;&nbsp;
                                                </div>
                                            </div>

                                            {params.search && (
                                                <Table
                                                    columns={{ name: 'Наименование товара', code: 'Штрих-код', quantity: 'Количество', actions: '' }}
                                                    items={products.filter((prod) => !values.products.map((p) => p?.id).includes(prod?.id))}
                                                    renderItem={(item) => {
                                                        console.log('item')

                                                        return (
                                                            <tr key={item?.id}>
                                                                <td>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        {item?.image ? (
                                                                            <img
                                                                                onClick={() => {
                                                                                    setSelectedImage(domain + item.image)
                                                                                    setIsModalOpen(true)
                                                                                }}
                                                                                src={domain + item.image}
                                                                                alt={item.name}
                                                                                style={imageStyle}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                onClick={() => {
                                                                                    setSelectedImage(null)
                                                                                    setIsModalOpen(true)
                                                                                }}
                                                                                style={defaultImageStyle}
                                                                            >
                                                                                <ion-icon name="image-outline" />

                                                                            </div>
                                                                        )}
                                                                        <span>{item?.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td>{item?.barcode}</td>
                                                                <td>
                                                                    {item?.count} {getMeasurement(item?.measurement)}
                                                                </td>
                                                                <td>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>

                                                                        <Button onClick={() => showProductEdit({ item })} icon="create-outline" className="is-size-7 is-outlined is-link " disabled={loading} />
                                                                        <Button onClick={() => showDeleteProduct(item)} icon="trash-outline" className="is-size-7 is-danger is-outlined" disabled={loading} />

                                                                        {item?.paymentType === values?.currency ? (
                                                                            <Button
                                                                                onClick={() => {
                                                                                    const localItems = JSON.parse(localStorage.getItem('items')) || []
                                                                                    const productCurrency = item?.paymentType

                                                                                    const bodyPrice = productCurrency === 'sum' ? item?.sumPrice : item?.usdPrice
                                                                                    const SalePrice = productCurrency === 'sum' ? item?.sumPriceSale : item?.usdPriceSale
                                                                                    const SaleOptomPrice = productCurrency === 'sum' ? item?.sumPriceTransfer : item?.usdPriceTransfer

                                                                                    localItems.push({ ...item, price: bodyPrice, priceSale: SalePrice, priceTransfer: SaleOptomPrice })
                                                                                    localStorage.setItem('items', JSON.stringify(localItems))
                                                                                    arrayHelpers.push({ ...item, price: bodyPrice, priceSale: SalePrice, priceTransfer: SaleOptomPrice })
                                                                                }}
                                                                                icon="add-outline"
                                                                                className="is-size-7 is-outlined is-success"
                                                                                disabled={loading}
                                                                            />
                                                                        ) : (
                                                                            <span className="has-text-danger">Выберите другую валюту для добавления</span>
                                                                        )}
                                                                    </div>

                                                                </td>
                                                            </tr>
                                                        )
                                                    }}
                                                />
                                            )}
                                        </>
                                    )
                                }}
                            />

                            <div className="my-4">
                                <div className="is-pulled-left">
                                    <span className="tag is-large is-light mr-2">Общее количество: {totals.totalQuantity.toFixed(2)}</span>
                                    <span className="tag is-large is-light mr-2">Сумма прихода: {totals.totalPurchaseSum.toFixed(2)}</span>
                                    <span className="tag is-large is-light">Сумма продаж: {totals.totalSaleSum.toFixed(2)}</span>
                                </div>
                                <div className="is-pulled-right">
                                    <Button onClick={onCancel} icon="close-outline" text="Отмена" className="is-danger" disabled={loading} />
                                    &nbsp;&nbsp;
                                    <Button text="Сохранить" icon="add-outline" type="submit" className="is-success" disabled={loading} />
                                </div>
                            </div>

                            <br />

                            <ReactModal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)}>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                    {selectedImage ? (
                                        <img src={selectedImage} alt="Selected" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                    ) : (
                                        <div style={{ textAlign: 'center' }}>
                                            <i className="fas fa-image" style={{ fontSize: '3rem', color: '#ddd' }} />
                                            <p style={{ color: '#999' }}>Изображение недоступно</p>
                                        </div>
                                    )}
                                </div>
                            </ReactModal>
                        </Card>
                    </Form>
                )
            }}
        </Formik>
    )
}
